import React, { FC, useState } from 'react';
import { Auth } from '@aws-amplify/auth';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  LightMode,
  Stack,
  Text,
  useColorModeValue as mode
} from '@chakra-ui/react';
import { emailForm, nameRegExp, passwordRegExp, tempEmail } from '../../libs/utils';
import { UnderlineLink } from '../chakraUIPro/UnderlineLink';
import { useAnalytics } from '../../contexts/AnalyticsContext';

type SignUpFormProps = {
  // eslint-disable-next-line no-unused-vars
  callback(err: string | null, user: { username: string; password: string }): void;
};

const SignUpForm: FC<SignUpFormProps> = ({ callback }: SignUpFormProps) => {
  const { logEvent } = useAnalytics();
  const [error, setError] = useState('');

  async function register(evt: any) {
    setError('');

    const { email, password, fName, lName, orgName } = evt;

    try {
      const authResp = await Auth.signUp({
        password,
        username: email.toLowerCase(),
        attributes: {
          email: email.toLowerCase(),
          name: fName,
          family_name: lName,
          'custom:org': orgName
        }
      });

      // eslint-disable-next-line no-console
      console.info({
        action: 'register:Auth.signup:resp',
        data: { authResp, user: { username: email.toLowerCase() } }
      });

      callback(null, { password, username: email });

      logEvent({
        action: 'Signed up',
        category: 'Sign up'
      });
    } catch (err) {
      console.error({ action: 'register:error', error: err });
      setError(err.message);
    }
  }

  const stage = process.env.NEXT_PUBLIC_STAGE;
  const registerSchema = yup.object({
    password: yup
      .string()
      .matches(passwordRegExp, {
        message: (cur) => {
          const fails: string[] = [];
          if (!cur.value.match(/[a-z]/)) {
            fails.push('lowercase');
          }
          if (!cur.value.match(/[A-Z]/)) {
            fails.push('uppercase');
          }
          if (!cur.value.match(/[0-9]/)) {
            fails.push('number');
          }
          if (!cur.value.match(/[!@#$%^&*\-_.]/)) {
            fails.push('special character');
          }
          if (cur.value.match(/^\s/)) {
            fails.push('no spaces at the start or end');
          }

          return `Password must include ${fails.join(', ')}`;
        }
      })
      .min(8, 'Minimum length is 8 characters')
      .required('Password is required'),
    email: yup
      .string()
      .email('Must be a valid email')
      .required('Email is required')
      .matches(stage !== 'prod' ? tempEmail : emailForm, `Sign-up is not available at the moment`),
    fName: yup
      .string()
      .matches(nameRegExp, 'It looks like your name contains invalid characters')
      .required('First name is required'),
    lName: yup
      .string()
      .matches(nameRegExp, 'It looks like your name contains invalid characters')
      .required('Last name is required')
    // orgName: yup
    //   .string()
    //   .matches(nameRegExp, 'It looks like your name contains invalid characters')
    //   .required('Organisation name is required')
  });

  return (
    <Formik
      validationSchema={registerSchema}
      // eslint-disable-next-line react/jsx-no-bind
      onSubmit={register}
      initialValues={{
        email: '',
        password: '',
        fName: '',
        lName: '',
        orgName: ''
      }}
    >
      {({ isSubmitting, handleChange, values, touched, errors, handleBlur }) => (
        <Form>
          <Flex align='center' justify='space-between' mb='8'>
            <Box />
            {/* <Link href='/sign-in'> */}
            <UnderlineLink fontSize='sm' href='/sign-in/'>
              Already have an account?
            </UnderlineLink>
            {/* </Link> */}
          </Flex>

          {/* Errors */}
          {error.length > 0 && (
            <Alert status='error' mb='4' borderRadius='md'>
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Stack spacing='-px' mb='2rem'>
            {/* Email */}
            <FormControl
              // TODO:
              // @ts-ignore
              value={values.email}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.email && !!errors.email}
            >
              <FormLabel srOnly>Email</FormLabel>
              <Input
                id='email'
                size='lg'
                name='email'
                type='email'
                autoComplete='email'
                required
                placeholder='Email address'
                bg={mode('white', 'gray.700')}
                fontSize='md'
                roundedBottom='0'
                focusBorderColor='blue.300'
              />
            </FormControl>

            {/* Password */}
            <FormControl
              // TODO:
              // @ts-ignore
              value={values.password}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.password && !!errors.password}
            >
              <FormLabel srOnly>Password</FormLabel>
              <Input
                id='password'
                name='password'
                type='password'
                autoComplete='new-password'
                required
                size='lg'
                bg={mode('white', 'gray.700')}
                fontSize='md'
                roundedTop='0'
                placeholder='Password'
                focusBorderColor='blue.300'
              />
              <FormErrorMessage>
                <>
                  {errors.email}
                  <br />
                  {errors.password}
                </>
              </FormErrorMessage>
            </FormControl>
          </Stack>

          <Text color='gray.400'>...and to help us personalise the service</Text>
          <Stack spacing='-px' pt='4'>
            {/* First name */}
            <FormControl
              // TODO:
              // @ts-ignore
              value={values.fName}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.fName && !!errors.fName}
            >
              <FormLabel srOnly>First name</FormLabel>
              <Input
                id='fName'
                size='lg'
                name='fName'
                type='text'
                autoComplete='given-name'
                required
                placeholder='First name'
                bg={mode('white', 'gray.700')}
                fontSize='md'
                roundedBottom='0'
                focusBorderColor='blue.300'
              />
            </FormControl>

            {/* Last name */}
            <FormControl
              // TODO:
              // @ts-ignore
              value={values.lName}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.lName && !!errors.lName}
            >
              <FormLabel srOnly>Last name</FormLabel>
              <Input
                id='lName'
                size='lg'
                name='lName'
                type='text'
                autoComplete='family-name'
                placeholder='Last name'
                bg={mode('white', 'gray.700')}
                fontSize='md'
                roundedTop='0'
                focusBorderColor='blue.300'
              />
              <FormErrorMessage>
                <>
                  {errors.fName}
                  <br />
                  {errors.lName}
                  <br />
                  {/* {errors.orgName} */}
                </>
              </FormErrorMessage>
            </FormControl>

            {/* Org */}
            {/* <FormControl
              // @ts-ignore
              value={values.orgName}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={touched.orgName && !!errors.orgName}
            >
              <FormLabel srOnly>Org number</FormLabel>
              <Input
                id='orgName'
                size='lg'
                name='orgName'
                type='text'
                required
                autoComplete='Organisation Name'
                placeholder='Organisation Name'
                bg={mode('white', 'gray.700')}
                fontSize='md'
                roundedTop='0'
                focusBorderColor='orange.300'
              />
            </FormControl> */}
          </Stack>

          <LightMode>
            <Button
              size='lg'
              type='submit'
              mt='8'
              w='full'
              colorScheme='blue'
              fontSize='md'
              fontWeight='bold'
              isLoading={isSubmitting}
              isDisabled={!!errors.email || !!errors.password || !!errors.fName}
            >
              Sign Up
            </Button>
          </LightMode>
        </Form>
      )}
    </Formik>
  );
};

export default SignUpForm;
